import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService } from './base-api.service';
import {
    SystemArticleModel,
    CreateRelationshipRequestModel
} from '../models';

/**
 * System Articles API Service
 */
@Injectable({
    providedIn: 'root'
})
export class SystemArticlesService extends BaseApiService {

    /**
     * Get articles for a system
     */
    getSystemArticles(systemId: string): Observable<SystemArticleModel[]> {
        return this.get<SystemArticleModel[]>(`/SystemArticles/systems/${systemId}/articles`)
            .pipe(map(response => response.data!));
    }

    /**
     * Get systems for an article
     */
    getArticleSystems(articleId: string): Observable<SystemArticleModel[]> {
        return this.get<SystemArticleModel[]>(`/SystemArticles/articles/${articleId}/systems`)
            .pipe(map(response => response.data!));
    }

    /**
     * Check if relationship exists
     */
    checkRelationshipExists(systemId: string, articleId: string): Observable<boolean> {
        return this.get<boolean>(`/SystemArticles/systems/${systemId}/articles/${articleId}/exists`)
            .pipe(map(response => response.data!));
    }

    /**
     * Get specific system-article relationship
     */
    getRelationship(systemId: string, articleId: string): Observable<SystemArticleModel> {
        return this.get<SystemArticleModel>(`/SystemArticles/systems/${systemId}/articles/${articleId}`)
            .pipe(map(response => response.data!));
    }

    /**
     * Delete system-article relationship
     */
    deleteRelationship(relationshipId: string): Observable<void> {
        return this.delete<void>(`/SystemArticles/relationships/${relationshipId}`)
            .pipe(map(() => undefined));
    }

    /**
     * Activate system-article relationship
     */
    activateRelationship(systemId: string, articleId: string): Observable<SystemArticleModel> {
        return this.post<SystemArticleModel>(`/SystemArticles/systems/${systemId}/articles/${articleId}/activate`)
            .pipe(map(response => response.data!));
    }

    /**
     * Bulk create system-article relationships
     */
    bulkCreateRelationships(systemId: string, relationships: CreateRelationshipRequestModel[]): Observable<SystemArticleModel[]> {
        return this.post<SystemArticleModel[]>(`/SystemArticles/systems/${systemId}/articles/bulk`, relationships)
            .pipe(map(response => response.data!));
    }

    /**
     * Get count of articles for a system
     */
    getSystemArticleCount(systemId: string): Observable<number> {
        return this.get<number>(`/SystemArticles/systems/${systemId}/count`)
            .pipe(map(response => response.data!));
    }

    /**
     * Get count of systems for an article
     */
    getArticleSystemCount(articleId: string): Observable<number> {
        return this.get<number>(`/SystemArticles/articles/${articleId}/count`)
            .pipe(map(response => response.data!));
    }
}
